import * as Sentry from "@sentry/react-router";
import { createContext } from "react";
import type { Optional } from "@/types";

declare const __VERSION__: string | undefined;

export const SentryEnvironmentKeys = {
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#dsn */
  DSN: "SENTRY_DSN",
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#environment */
  ENVIRONMENT: "SENTRY_ENVIRONMENT",
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#release */
  VERSION: "SENTRY_VERSION",
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#sampleRate */
  SAMPLE_RATE: "SENTRY_SAMPLE_RATE",
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#tracesSampleRate */
  TRACES_SAMPLE_RATE: "SENTRY_TRACES_SAMPLE_RATE",
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#replaysSessionSampleRate */
  REPLAYS_SESSION_SAMPLE_RATE: "SENTRY_REPLAYS_SESSION_SAMPLE_RATE",
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#org */
  ORG: "SENTRY_ORG",
  /** https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#project */
  PROJECT: "SENTRY_PROJECT",
} as const;

declare global {
  interface Window {
    [SentryEnvironmentKeys.DSN]: string;
    [SentryEnvironmentKeys.ENVIRONMENT]: string;
    [SentryEnvironmentKeys.VERSION]: string;
    [SentryEnvironmentKeys.SAMPLE_RATE]: string;
    [SentryEnvironmentKeys.TRACES_SAMPLE_RATE]: string;
    [SentryEnvironmentKeys.REPLAYS_SESSION_SAMPLE_RATE]: string;
  }
}

export const SentryConfigurationContext = createContext<SentryConfiguration | null>(
  null,
);

export const SentryConfigurationScript = (props: {
  nonce: string;
  sentryConfiguration: SentryConfiguration;
}) => {
  return (
    <script
      id={"sentry-configuration-script"}
      nonce={props.nonce}
      dangerouslySetInnerHTML={{
        __html: [
          `window["${SentryEnvironmentKeys.DSN}"]="${props.sentryConfiguration.dsn ?? ""}"`,
          `window["${SentryEnvironmentKeys.ENVIRONMENT}"]="${props.sentryConfiguration.environment ?? ""}"`,
          `window["${SentryEnvironmentKeys.VERSION}"]="${props.sentryConfiguration.version ?? ""}"`,
          `window["${SentryEnvironmentKeys.SAMPLE_RATE}"]="${props.sentryConfiguration.sampleRate ?? ""}"`,
          `window["${SentryEnvironmentKeys.TRACES_SAMPLE_RATE}"]="${props.sentryConfiguration.tracesSampleRate ?? ""}"`,
          `window["${SentryEnvironmentKeys.REPLAYS_SESSION_SAMPLE_RATE}"]="${props.sentryConfiguration.replaysSessionSampleRate ?? ""}"`,
        ].join(";"),
      }}
    />
  );
};

type SentryConfiguration = {
  dsn?: Optional<string>;
  environment?: Optional<string>;
  version?: Optional<string>;
  sampleRate: Optional<number>;
  tracesSampleRate: Optional<number>;
  replaysSessionSampleRate: Optional<number>;
};

export const initSentry = (options: SentryConfiguration) => {
  if (!options.dsn || !options.environment) {
    return;
  }

  Sentry.init({
    dsn: options.dsn,
    environment: options.environment,
    release: options.version ?? "",
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#sendDefaultPii
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    // Enable logs to be sent to Sentry
    enableLogs: true,

    sampleRate: options.sampleRate ?? 1.0,
    tracesSampleRate: options.tracesSampleRate ?? 0.1,
    replaysSessionSampleRate: options.replaysSessionSampleRate ?? 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
};
